import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getPlan, type PricingPlanId } from '../../data/pricingPlans';
interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: PricingPlanId;
}
export function CancelSubscriptionModal({
  isOpen,
  onClose,
  currentPlan
}: CancelSubscriptionModalProps) {
  const {
    user,
    updateUserProfile
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const handleCancelSubscription = async () => {
    if (!user?.id) {
      setError('You must be logged in to cancel subscription');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Call backend to cancel subscription
      const response = await fetch('https://project-observant-artichoke-789.magicpatterns.app/functions/v1/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      // Update user profile to free plan
      await updateUserProfile({
        subscriptionPlan: 'free'
      });
      // Close modal and show success
      onClose();
      window.location.reload(); // Refresh to show updated plan
    } catch (err: any) {
      console.error('Cancellation error:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1928" data-mp="1yr3vc8r0exs9">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" data-id="element-1929" data-mp="3221c2nfldrve">
        <div className="flex items-center justify-between mb-4" data-id="element-1930" data-mp="324dpcnaene24">
          <div className="flex items-center" data-id="element-1931" data-mp="2x4hv3frif0zr">
            <div className="bg-red-100 p-2 rounded-lg mr-3" data-id="element-1932" data-mp="3b2zfb3i7hvcx">
              <AlertTriangle size={24} className="text-red-600" data-id="element-1933" data-mp="3b4iwqxflmcga" />
            </div>
            <h2 className="text-xl font-bold" data-id="element-1934" data-mp="370b2yluaa6b7">Cancel Subscription</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1935" data-mp="3l0dkg6yoixpx">
            <X size={24} data-id="element-1936" data-mp="3g1shw0vyg1yb" />
          </button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-id="element-1937" data-mp="3upnc5lpm2edi">
            <p className="text-red-800 text-sm" data-id="element-1938" data-mp="3prlun57u2n45">{error}</p>
          </div>}

        <div className="mb-6" data-id="element-1939" data-mp="7lzaqcnik2tx">
          <p className="text-gray-700 mb-4" data-id="element-1940" data-mp="7n0oiovwz3fc">
            Are you sure you want to cancel your{' '}
            <strong data-id="element-1941" data-mp="2oh0mixrfhkc">{getPlan(currentPlan)?.name ?? 'current'}</strong>{' '}
            subscription?
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4" data-id="element-1942" data-mp="m9hcp39oyjcn">
            <h3 className="font-semibold text-yellow-900 mb-2" data-id="element-1943" data-mp="r6pw176kstfh">
              You will lose access to:
            </h3>
            <ul className="space-y-2 text-sm text-yellow-800" data-id="element-1944" data-mp="d7zt3ax2rzj7">
              {currentPlan !== 'single_scan' ? <>
                  <li data-id="element-1945" data-mp="3iq25ydywmwo">• Unlimited scans</li>
                  <li data-id="element-1946" data-mp="3gvhml6lywt4">• Advanced pattern detection</li>
                  <li data-id="element-1947" data-mp="88wry1k7udgi">• Detailed PDF reports</li>
                  <li data-id="element-1948" data-mp="8arbbbfu9xih">• Priority support</li>
                </> : <>
                  <li data-id="element-1949" data-mp="3prhl2jj5sqqs">• Paid scan credits</li>
                  <li data-id="element-1950" data-mp="3uohosnsjdheq">• Full AI insights & analysis</li>
                  <li data-id="element-1951" data-mp="3up0xddwaqtg3">• Detailed pattern labels</li>
                  <li data-id="element-1952" data-mp="3gogi2rplbfrc">• Personalized advice</li>
                </>}
            </ul>
          </div>

          <div className="flex items-start mb-4" data-id="element-1953" data-mp="3ln1fkjdgjlc3">
            <input type="checkbox" id="confirm-cancel" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" data-id="element-1954" data-mp="3lpvz88ond24g" />
            <label htmlFor="confirm-cancel" className="text-sm text-gray-700" data-id="element-1955" data-mp="33vhdfhmxeg0k">
              I understand that I will lose access to premium features and my
              subscription will be cancelled immediately.
            </label>
          </div>
        </div>

        <div className="flex gap-3" data-id="element-1956" data-mp="2px16pqp2cpif">
          <button onClick={onClose} disabled={loading} className="flex-1 bg-gray-100 text-gray-700 rounded-lg py-3 font-medium hover:bg-gray-200 transition-colors disabled:opacity-50" data-id="element-1957" data-mp="2q05hj6x1aji2">
            Keep Subscription
          </button>
          <button onClick={handleCancelSubscription} disabled={!confirmed || loading} className="flex-1 bg-red-600 text-white rounded-lg py-3 font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1958" data-mp="j6jsmb068jw1">
            {loading ? 'Cancelling...' : 'Cancel Subscription'}
          </button>
        </div>
      </div>
    </div>;
}